/**
 * 评分分发。一个入口，按 descriptor 的 structure 分派：叶子交给题型自己判，组合题递归后合并。
 * 这里没有 switch 链，加题型不改本文件。
 * 知识点卡不进本模块：它没有题面答案，自评档位直接进 scheduling 的 advance。
 * 用 model 与同目录的 question-types，被 session 与 workspace 调用，不定义题型，也不推进间隔。
 */

import type { Score } from '../model/result'
import { partial } from '../model/result'
import type { GradeOutcome, GroupDescriptor, LeafDescriptor } from './question-types'
import { DESCRIPTORS } from './question-types'
import type { AnyQuestion, LeafKind, QuestionAnswer } from '../model/question'

function kindMismatch(question: AnyQuestion, answer: QuestionAnswer): Error {
	return new Error(`grading.gradeAnswer: 题 ${question.id} 的题型是 ${question.kind}，答案的 kind 是 ${answer.kind}`)
}

/**
 * 单一评分入口。
 * answer.kind 与 question.kind 不一致只可能是程序错误：答案不持久化，UI 是照着题目树构造出来的，
 * 所以这里直接抛，不为它设计降级路径。
 *
 * 分派先看 answer.kind，因为编译器只对字面量比较做判别联合收窄。
 * 两处 `as` 是同一件事的代价：kind、data、value 三者同源这条事实类型表达不了，
 * 而 TypeDescriptor<K> 里那半个永不构造的分支也只能在这里收掉。
 */
export function gradeAnswer(question: AnyQuestion, answer: QuestionAnswer): GradeOutcome {
	if (answer.kind === 'reading') {
		if (question.kind !== 'reading') throw kindMismatch(question, answer)
		const group = DESCRIPTORS.reading as GroupDescriptor<'reading'>
		const outcomes = question.data.questions.map((child, index) => {
			const childAnswer = answer.value[index]
			if (childAnswer === undefined) {
				throw new Error(`grading.gradeAnswer: 材料题 ${question.id} 的第 ${index + 1} 个子题没有答案`)
			}
			// Question<LeafKind> 是 kind 与 data 各自取并集，AnyQuestion 是整对取并集，前者比后者宽。
			// 子题由 parsing 构造，kind 与 data 本来就同源，这里只是把编译器读不出的那层同构收回来。
			return gradeAnswer(child as AnyQuestion, childAnswer)
		})
		return group.combine(outcomes)
	}
	if (answer.kind !== question.kind) throw kindMismatch(question, answer)
	const leaf = DESCRIPTORS[question.kind] as LeafDescriptor<LeafKind>
	return leaf.grade(question.data, answer.value)
}

/** 讲评视图需要的东西：一道题的完整反馈，含解析文本。放这里是为了让屏幕只调一次。 */
export interface Feedback {
	readonly outcome: GradeOutcome
	readonly explain: string | null
}

export function feedbackFor(question: AnyQuestion, answer: QuestionAnswer): Feedback {
	return { outcome: gradeAnswer(question, answer), explain: question.explain }
}

/** 组合题的分数区间归一化，唯一一次除法。 */
export function average(children: readonly GradeOutcome[]): Score {
	const sum = children.reduce((total, child) => total + child.score, 0)
	return partial(sum, children.length)
}
